import { Tag } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { paths } from 'routes/routes';

/**
 * Liên kết chéo từ MỘT người dùng (trang Thành viên → "Xem nhanh") sang các trang
 * nhân sự, bộ lọc đặt sẵn trên URL:
 *
 *   ?userId=<id>&name=<tên hiển thị>  Nghỉ phép, Tăng ca, Phiếu lương, Sổ Sao
 *   ?username=<TikTok ID>             Chấm công (API /attendance/list không nhận userId)
 *
 * `name` chỉ để hiển thị trên trang đích, KHÔNG gửi lên API.
 * Trang đích không có tham số thì chạy y như trước.
 */

export interface LinkableUser {
  id: number;
  username?: string | null;
  nickName?: string | null;
  email?: string | null;
}

export function userDisplayName(user: LinkableUser): string {
  return user.nickName || user.username || user.email || `#${user.id}`;
}

function withQuery(path: string, query: Record<string, string>) {
  return `${path}?${new URLSearchParams(query).toString()}`;
}

export function buildUserLinks(user: LinkableUser) {
  const byId = { userId: String(user.id), name: userDisplayName(user) };
  const attendanceQuery: Record<string, string> | null = user.username
    ? { username: user.username }
    : user.email
    ? { email: user.email }
    : null;
  return {
    attendance: attendanceQuery ? withQuery(paths.attendance, attendanceQuery) : undefined,
    leaveRequests: withQuery(paths.leaveRequests, byId),
    overtime: withQuery(paths.overtime, byId),
    payslips: withQuery(paths.payslips, byId),
    starLedger: withQuery(paths.starLedger, byId),
  };
}

export function toPositiveInt(raw: string | null): number | undefined {
  const value = Number(raw);
  return raw && Number.isInteger(value) && value > 0 ? value : undefined;
}

/** Giá trị hợp lệ của enum trạng thái; sai/`ALL` → undefined (= tất cả). */
export function toEnumValue<T extends string>(
  values: Record<string, T>,
  raw: string | null
): T | undefined {
  const upper = raw?.trim().toUpperCase();
  return (Object.values(values) as string[]).includes(upper || '') ? (upper as T) : undefined;
}

/** Gỡ vài tham số khỏi URL, giữ nguyên các tham số khác (vd. `?tab=` của TabbedPage). */
export function useDropSearchParams() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (...keys: string[]) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          keys.forEach((key) => next.delete(key));
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
}

/**
 * Gọi `onChange(next, prev)` khi một tham số URL đổi SAU lần render đầu: đóng thẻ
 * "Đang lọc", bấm lại menu (URL mất tham số), Back/Forward sang người khác.
 * Lần đầu không gọi — giá trị ban đầu đã nằm trong state khởi tạo của trang.
 */
export function useParamChange<T>(value: T, onChange: (next: T, prev: T) => void) {
  const prevRef = useRef(value);
  useEffect(() => {
    const prev = prevRef.current;
    if (prev === value) return;
    prevRef.current = value;
    onChange(value, prev);
    // Chỉ chạy khi giá trị đổi; onChange là hàm mới mỗi lần render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
}

interface LinkedFilterTagProps {
  label: string;
  onClose: () => void;
}

/** Thẻ "Đang lọc: <tên>" — bấm × để bỏ lọc theo người này. */
export const LinkedFilterTag = ({ label, onClose }: LinkedFilterTagProps) => (
  <div style={{ marginBottom: 12 }}>
    <Tag
      color="magenta"
      icon={<FilterOutlined />}
      closable
      onClose={(event) => {
        // Trang tự ẩn thẻ khi bỏ lọc; chặn antd tự ẩn để hai bên không lệch nhau.
        event.preventDefault();
        onClose();
      }}
      style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}
    >
      Đang lọc: <strong>{label}</strong>
    </Tag>
  </div>
);
